import { format, parse } from 'date-fns';
import { router, useLocalSearchParams } from 'expo-router';
import { capitalize } from 'lodash';
import { StyleSheet } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import { useCreateNpoPaymentAccountSteps } from '@/api/hooks/paymentAccountsHooks';
import { NpoPaymentAccountStepsResponse, OwnerPrincipal } from '@/api/models/paymentAccountsModels';
import FormWrapper from '@/components/forms/FormWrapper';
import OwnerForm from '@/components/forms/paymentNpoOnboarding/OwnerForm';
import { useAuth } from '@/contexts/authContext';
import { useEndFlowModal } from '@/contexts/endFlowModalContext';
import { useNpoMerchantOnboardingState } from '@/contexts/npoMerchantOnboardingContext';
import { OwnerData } from '@/lib/validations/NpoPaymentOnboarding';
import { addressFromString, validateAddress } from '@/utils/addressFromString';
import { getPrimaryZipCode } from '@/utils/getPrimaryZipCode';
import { i18n } from '@/utils/i18n';
import { safeJsonParse } from '@/utils/jsonParse';
import { normalizePhone } from '@/utils/normalizePhone';

const Owner = () => {
  const { npoItem } = useLocalSearchParams<{
    npoItem: string;
  }>();

  const { state: merchantState, setInitialNpoMerchantData, setNpoId } = useNpoMerchantOnboardingState();
  const merchantStateResponse = safeJsonParse<NpoPaymentAccountStepsResponse>(
    merchantState.initialNpoMerchantData ?? ''
  );

  const { state: authState } = useAuth();

  const { mutateAsync: saveStep, isPending } = useCreateNpoPaymentAccountSteps();

  const { showSuccess } = useEndFlowModal();

  const ownerData = merchantStateResponse?.result?.ownerPrincipalData;
  const initialData: OwnerData = {
    firstName: ownerData?.first_name ?? authState.user?.user?.firstName ?? '',
    middleName: ownerData?.middle_name ?? '',
    lastName: ownerData?.last_name ?? authState.user?.user?.lastName ?? '',
    emailAddress: ownerData?.email_address ?? authState.user?.user?.email ?? '',
    phone: normalizePhone(ownerData?.phone) ?? normalizePhone(authState.user?.user?.phone) ?? '',
    dateOfBirth:
      ownerData?.date_of_birth !== undefined
        ? parse(ownerData.date_of_birth, 'MM/dd/yyyy', new Date()).toISOString()
        : '',
    socialSecurityNumber: ownerData?.social_security_number ?? '',
    driversLicenseNumber: ownerData?.drivers_license_number ?? '',
    address: validateAddress(ownerData?.address) ?? '',
    signingManager: ownerData?.signing_manager === 'True' ? true : false
  };

  const parseOwnerPrincipalData = (data: OwnerData): OwnerPrincipal => {
    const formattedAddress = addressFromString(data.address ?? '');

    const parsedOwnerPrincial = {
      first_name: data.firstName,
      middle_name: data.middleName ?? '',
      last_name: data.lastName,
      email_address: data.emailAddress,
      phone: data.phone,
      date_of_birth: format(new Date(data.dateOfBirth), 'MM/dd/yyyy'),
      social_security_number: data.socialSecurityNumber,
      drivers_license_number: data.driversLicenseNumber,
      address: {
        address: formattedAddress?.address ?? '',
        postal_code: getPrimaryZipCode(formattedAddress?.postal_code ?? ''),
        city: formattedAddress?.city ?? '',
        state: formattedAddress?.state ?? ''
      },
      signing_manager: capitalize(data.signingManager.toString())
    };

    return parsedOwnerPrincial;
  };

  return (
    <SafeAreaView style={style.flexOne} edges={['bottom']}>
      <FormWrapper scrollEnable minHeightMinus={180}>
        <OwnerForm
          initialData={initialData}
          isLoading={isPending}
          onDone={(data) => {
            const parsedOwnerPrincial = parseOwnerPrincipalData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  owner_Principal: parsedOwnerPrincial
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );

                  router.navigate({
                    pathname: '/npoPaymentOnboarding/riskProfile',
                    params: {
                      npoItem
                    }
                  });
                }
              }
            );
          }}
          onSaveAndContinue={(data) => {
            const parsedOwnerPrincial = parseOwnerPrincipalData(data);

            saveStep(
              {
                type: 'both',
                body: {
                  nonProfitOrgId: merchantState.npoId ?? '',
                  owner_Principal: parsedOwnerPrincial
                }
              },
              {
                onSuccess: (data) => {
                  setNpoId(data.nonProfitOrgId);
                  setInitialNpoMerchantData(
                    JSON.stringify({
                      files: [],
                      result: data
                    })
                  );
                  showSuccess({
                    title: i18n.t('profile.saved_continue_title'),
                    message: i18n.t('profile.saved_continue_description'),
                    onButtonPress: () => {
                      router.dismissAll();
                      router.navigate('/(app)/(bottomTabs)/profile/npoProfile/insights');
                    }
                  });
                }
              }
            );
          }}
        />
      </FormWrapper>
    </SafeAreaView>
  );
};

export default Owner;

const style = StyleSheet.create({
  flexOne: {
    flex: 1
  }
});
